import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { HubConnection } from '@microsoft/signalr'
import { acquire, release } from './connection'
import { liveKeys } from './queries'
import type { SessionAnnouncement } from './types'

/**
 * How a player learns a fight has started.
 *
 * The shell holds this for as long as someone is signed in. It rides the connection the app
 * already has rather than a second one — the whole reason announcements went on the hub rather
 * than becoming a Web Push stage of their own
 * (`docs/web-api/phase-9-kickoff.md` §1, decision 6).
 *
 * The API pushes to a `campaign-{id}` group that every connection joins on connect, so this
 * needs no subscription bookkeeping: being in the campaign is the subscription.
 */
export function useAnnouncements(enabled: boolean): {
  latest: SessionAnnouncement | null
  dismiss: () => void
} {
  const queryClient = useQueryClient()
  const [latest, setLatest] = useState<SessionAnnouncement | null>(null)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let connection: HubConnection | null = null

    const onStarted = (announcement: SessionAnnouncement) => {
      if (cancelled) return

      setLatest(announcement)
      // The Live tab appears off the list, so it has to learn about this too.
      void queryClient.invalidateQueries({ queryKey: liveKeys.list() })
    }

    void (async () => {
      const active = await acquire()

      if (cancelled) {
        release()
        return
      }

      connection = active
      active.on('SessionStarted', onStarted)
    })()

    return () => {
      cancelled = true

      if (connection === null) return

      connection.off('SessionStarted', onStarted)
      release()
    }
  }, [enabled, queryClient])

  return { latest, dismiss: () => setLatest(null) }
}
